import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { trackPolicyPageVisit } from './userJourneyTracker';

const linkStyle = { color: '#5170ff', textDecoration: 'none' };

function YouTubeApiPrivacySection({ paragraphStyle, listStyle }) {
  return (
    <section id="youtube-api-services" style={{ marginBottom: '28px' }}>
      <h2 style={{ marginBottom: '10px', fontSize: '1.2rem', color: '#1e293b', fontFamily: "'Montserrat', sans-serif" }}>
        YouTube API Services
      </h2>
      <p style={{ ...paragraphStyle, marginBottom: '12px' }}>
        Makereels.live uses YouTube API Services to let you connect your YouTube channel, schedule or publish
        Shorts, and manage connected-account information. When you choose to connect YouTube, we access Google
        user data only as needed to provide those features.
      </p>
      <p style={{ ...paragraphStyle, marginBottom: '8px' }}>
        <strong>Information we access from YouTube</strong>
      </p>
      <ul style={listStyle}>
        <li>Your Google account email and basic profile information shown on the OAuth consent screen</li>
        <li>YouTube channel identifiers (such as channel ID, title, and custom URL)</li>
        <li>OAuth tokens required to upload or schedule videos on your behalf</li>
        <li>Metadata needed to publish or schedule content (for example, title and description you provide)</li>
      </ul>
      <p style={{ ...paragraphStyle, marginBottom: '8px', marginTop: '12px' }}>
        <strong>OAuth scopes requested</strong>
      </p>
      <ul style={listStyle}>
        <li>
          <code>youtube.upload</code> — to upload or schedule Shorts to your connected YouTube channel
        </li>
        <li>
          <code>youtube.readonly</code> — to read your channel information needed to confirm the connection
        </li>
      </ul>
      <p style={{ ...paragraphStyle, marginBottom: '12px', marginTop: '12px' }}>
        We use this information solely to operate the YouTube publishing and scheduling features you request.
        We do not sell Google user data, and we do not use it for advertising or unrelated profiling.
      </p>
      <p style={{ ...paragraphStyle, marginBottom: '12px' }}>
        Our use of information received from Google APIs adheres to the{' '}
        <a
          href="https://developers.google.com/terms/api-services-user-data-policy"
          target="_blank"
          rel="noopener noreferrer"
          style={linkStyle}
        >
          Google API Services User Data Policy
        </a>
        , including the Limited Use requirements. For more information about how Google handles your data, see the{' '}
        <a
          href="https://policies.google.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
          style={linkStyle}
        >
          Google Privacy Policy
        </a>
        .
      </p>
      <p style={{ ...paragraphStyle, marginBottom: '8px' }}>
        <strong>Revoking YouTube access</strong>
      </p>
      <p style={{ ...paragraphStyle, marginBottom: '12px' }}>
        You can disconnect YouTube at any time from the Calendar page in Makereels.live: open{' '}
        <Link to="/calender" style={linkStyle}>Calendar</Link>, select your connected YouTube channel, open the
        channel menu (⋮), and choose <strong>Disconnect</strong>. This removes the stored connection from our
        systems and stops future uploads or scheduling through Makereels.live.
      </p>
      <p style={{ ...paragraphStyle, marginBottom: '12px' }}>
        You can also revoke Makereels.live&apos;s access from your Google Account at{' '}
        <a
          href="https://security.google.com/settings/security/permissions"
          target="_blank"
          rel="noopener noreferrer"
          style={linkStyle}
        >
          security.google.com/settings/security/permissions
        </a>
        {' '}or{' '}
        <a
          href="https://myaccount.google.com/permissions"
          target="_blank"
          rel="noopener noreferrer"
          style={linkStyle}
        >
          myaccount.google.com/permissions
        </a>
        .
      </p>
      <p style={{ ...paragraphStyle, marginBottom: '8px' }}>
        <strong>Data retention, refresh, and deletion</strong>
      </p>
      <p style={{ ...paragraphStyle, marginBottom: '12px' }}>
        OAuth access tokens are refreshed automatically when needed for uploads or scheduling. Other YouTube API data
        we store (such as channel identifiers) is refreshed at least every 30 days while your account remains connected,
        or deleted if we can no longer access your account with your consent.
      </p>
      <p style={{ ...paragraphStyle, marginBottom: '12px' }}>
        When you disconnect YouTube, we revoke the stored OAuth token with Google, cancel pending YouTube schedules
        that have not yet published, and delete associated OAuth tokens and connected-account records from our systems
        promptly (within 7 calendar days at most). Deleting data stored by Makereels.live does not delete content on
        YouTube itself; to remove videos on YouTube, use YouTube or an authorized client that supports deletion.
      </p>
      <p style={{ ...paragraphStyle, marginBottom: '0' }}>
        To request deletion of your account data, including any remaining YouTube connection data, email{' '}
        <a href="mailto:Aksharshare@gmail.com" style={linkStyle}>Aksharshare@gmail.com</a>
        {' '}from the address linked to your Makereels.live account.
      </p>
    </section>
  );
}

const sections = [
  {
    title: 'Introduction',
    body: 'Welcome to Makereels.live ("we", "our", or "us"), an AI-powered platform that enables users to transform and edit long-form video content into shorter formats (the "Services"). This Privacy Policy describes how we collect, use, process, and disclose information in connection with your access to and use of the Services. By accessing or using the Services in any manner, you acknowledge that you have read, understood, and agree to the practices and policies described herein. Your continued use of the Services constitutes your acceptance of this Privacy Policy, including any updates or modifications made from time to time.'
  },
  {
    title: 'Information We Collect',
    body: 'We may collect and process various categories of information in connection with your use of the Services, including information that you provide directly as well as information collected automatically. This may include, without limitation, account-related information such as your email address, including when you authenticate through third-party login providers such as Google, as well as any preferences or settings associated with your account. In addition, we may collect content submitted by you through the Services, including but not limited to videos, links such as YouTube URLs, captions, text inputs, and any related metadata. We may also automatically collect certain technical and usage-related information, including device and browser characteristics, IP address, approximate location derived from such IP address, and information about how you interact with and navigate the Services, including features used, session activity, and other diagnostic or performance-related data. Where applicable, payment-related information may be processed by third-party providers in connection with subscription plans, and while we may receive limited transaction-related details, we do not store complete payment card information.'
  },
  {
    title: 'How We Use Information',
    body: 'We may use the information we collect for a variety of purposes related to the operation and improvement of the Services, including but not limited to providing, maintaining, and enhancing functionality, enabling the processing and transformation of user-submitted content through automated systems, monitoring usage patterns and system performance, communicating with users regarding updates, service-related information, or support inquiries, and detecting, preventing, or addressing misuse, fraud, or technical issues. In addition, information may be used in aggregated, anonymized, or otherwise processed forms to analyze trends, improve service quality, develop new features, and optimize user experience. Such uses may occur as reasonably necessary to operate the Services and support ongoing product development.'
  },
  {
    title: 'AI Processing',
    body: 'In order to provide the Services, content submitted by users may be processed using automated systems, including AI-based tools, which may perform various transformations and operations on such content. This processing may include, without limitation, editing, segmentation, formatting, transcription, caption generation, and other forms of automated content manipulation or restructuring. You acknowledge and agree that such processing is an inherent part of the Services and that outputs generated through automated systems may vary in quality, accuracy, or suitability depending on the nature of the input and other factors. We do not guarantee that any output generated through the Services will be error-free, complete, or appropriate for any particular purpose, and users are responsible for reviewing and evaluating outputs prior to use or distribution.'
  },
  {
    title: 'User Content and Responsibility',
    body: 'You are solely responsible for any content that you upload, submit, process, generate, or distribute through the Services. By using Makereels.live, you represent and warrant that you own or have obtained all necessary rights, licenses, permissions, and consents required to use, process, and distribute such content, including any rights related to intellectual property, privacy, publicity, or applicable laws and regulations. We do not verify, monitor, or guarantee the legality, ownership, accuracy, or permissions associated with user-submitted content, and Makereels.live shall not be responsible or liable for any content processed or generated through the Services. Any use of the Services that results in the violation of third-party rights or applicable laws is solely the responsibility of the user.'
  },
  {
    title: 'Data Sharing',
    body: 'We may disclose information to third parties as reasonably necessary in connection with the operation, provision, and improvement of the Services, including to service providers that support infrastructure, storage, analytics, payment processing, and related functionality, as well as to partners or integrations authorized by you. We may also disclose information where required to comply with applicable laws, regulations, legal processes, or governmental requests, or where we believe such disclosure is necessary to protect our rights, property, or safety, or that of others. In addition, information may be transferred or disclosed in connection with a business transaction, including but not limited to a merger, acquisition, reorganization, or sale of assets.'
  },
  {
    id: 'youtube-api',
    rich: true
  },
  {
    title: 'Cookies and Analytics',
    body: 'We may use cookies and similar technologies to collect information about your interactions with the Services, including to understand usage patterns, improve functionality, analyze performance, and support service optimization. Such technologies may store or retrieve information on your device and may be used in conjunction with analytics providers or similar tools. You may control or manage certain aspects of cookie usage through your browser settings, although disabling such technologies may impact the availability or functionality of certain features of the Services.'
  },
  {
    title: 'Data Retention',
    body: 'We retain information for as long as reasonably necessary to fulfill the purposes for which it was collected, including to provide and maintain the Services, comply with legal obligations, resolve disputes, enforce agreements, and support business operations. Retention periods may vary depending on the nature of the data, the context in which it was collected, and applicable legal or operational requirements. In certain cases, information may be retained in aggregated or non-identifiable form for longer periods.'
  },
  {
    title: 'Data Security',
    body: 'We implement reasonable technical, organizational, and administrative measures designed to protect information from unauthorized access, disclosure, alteration, or destruction. However, no method of transmission over the internet or method of storage is completely secure, and we cannot guarantee absolute security of any information transmitted to or stored by the Services. Users are encouraged to take appropriate precautions to protect their account credentials and devices.'
  },
  {
    title: 'Global Use and Data Transfers',
    body: 'Makereels.live is accessible to users globally, and by using the Services, you acknowledge that information may be processed, stored, and transferred across different jurisdictions, including locations outside your country of residence. Such processing may be carried out by us or by third-party service providers supporting the Services, and you consent to such transfers as necessary for the operation of the platform.'
  },
  {
    title: 'Your Rights',
    body: 'Depending on your location and applicable laws, you may have certain rights in relation to your personal data, including rights to access, correct, delete, or object to certain types of processing. Requests to exercise such rights may be submitted to us at Aksharshare@gmail.com, and we may take steps to verify your identity before responding to such requests.'
  },
  {
    title: "Children's Privacy",
    body: 'The Services are not directed toward children, and we do not knowingly collect personal data from individuals below the age required under applicable law. If we become aware that such data has been collected, we may take steps to delete it as reasonably practicable.'
  },
  {
    title: 'Fair Use and Service Integrity',
    body: 'While certain plans may be described as offering unlimited usage, such usage remains subject to fair and reasonable use principles intended to ensure the stability, performance, and availability of the Services for all users. We reserve the right to monitor usage patterns and, where necessary, take appropriate action, including limiting, restricting, or suspending access, in cases where usage is excessive, abusive, or otherwise inconsistent with normal or intended use of the Services.'
  },
  {
    title: 'Subscriptions',
    body: 'To cancel your subscription, refer to our Terms and Conditions or contact support.'
  },
  {
    title: 'Changes to This Policy',
    body: 'We may modify or update this Privacy Policy from time to time to reflect changes in our practices, technologies, or legal requirements. Any such updates will be effective upon posting, and continued use of the Services after such updates constitutes acceptance of the revised policy.'
  }
];

function PolicyPage() {
  useEffect(() => {
    trackPolicyPageVisit();
  }, []);

  const paragraphStyle = {
    margin: 0,
    lineHeight: 1.7,
    color: '#334155',
    textAlign: 'justify',
    fontFamily: "'Inter', sans-serif"
  };

  const listStyle = {
    margin: '0 0 0 1.25rem',
    padding: 0,
    lineHeight: 1.7,
    color: '#334155',
    fontFamily: "'Inter', sans-serif"
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: "'Inter', sans-serif", display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, padding: '48px 16px' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', background: '#ffffff', borderRadius: '16px', padding: '40px 28px', boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)' }}>
          <h1 style={{ margin: 0, fontSize: '2.75rem', color: '#0f172a', textAlign: 'center', fontFamily: "'Montserrat', sans-serif" }}>Privacy Policy</h1>
          <p style={{ marginTop: '12px', marginBottom: '24px', color: '#475569' }}>
            Effective Date: 19/03/2025
          </p>

          {sections.map((section) => (
            section.rich ? (
              <YouTubeApiPrivacySection
                key={section.id}
                paragraphStyle={paragraphStyle}
                listStyle={listStyle}
              />
            ) : (
              <section key={section.title} style={{ marginBottom: '28px' }}>
                <h2 style={{ marginBottom: '10px', fontSize: '1.2rem', color: '#1e293b', fontFamily: "'Montserrat', sans-serif" }}>{section.title}</h2>
                <p style={paragraphStyle}>{section.body}</p>
              </section>
            )
          ))}

          <section>
            <h2 style={{ marginBottom: '10px', fontSize: '1.2rem', color: '#1e293b', fontFamily: "'Montserrat', sans-serif" }}>Contact Us</h2>
            <p style={paragraphStyle}>
              If you have any questions, concerns, or requests regarding this Privacy Policy, you may contact us at{' '}
              <a href="mailto:Aksharshare@gmail.com" style={linkStyle}>Aksharshare@gmail.com</a>
              {' '}or via support chat icon.
            </p>
          </section>
        </div>
      </div>
      <footer className="footer">
        <div className="footer-content">
          <p className="footer-text">© 2026 Makereels.live. All rights reserved.</p>
          <nav className="footer-nav" aria-label="Legal">
            <Link to="/policy" className="footer-link">Privacy Policy</Link>
            <span className="footer-sep" aria-hidden="true">·</span>
            <Link to="/terms" className="footer-link">Terms of Service</Link>
            <span className="footer-sep" aria-hidden="true">·</span>
            <Link to="/refund" className="footer-link">Refund Policy</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}

export default PolicyPage;
